import {
  requestIsBusy,
  requestIsSuccess,
  requestResponse,
} from "@redriver/cinnamon";
import { produce } from "immer";
import { setDefaults } from "./SetDefaults";
import {
  calculateTotalCosts,
  calculateTotals,
  calculateTotalInvestment,
} from "../Calculation";
import {
  calculateServicesTotals,
  concatenateServiceArrays,
  getWizardSplitTotals,
  sumValues,
  getLFPItems,
} from "../../helpers";
import { updateServicesArrays } from "./UpdateServicesArrays";
import { checkSectionsCompleted } from "./CheckSectionsCompleted";
import { updateSettlementAndCoTerminusArrays } from "./UpdateSettlementAndCoTerminusArrays";
import { updateLargeFormatScheduleItems } from "../Schedules";
import { mergeData } from "./MergeData";
import { stringifyNullableBool } from "components/forms";
import { calculateComparisonSheetData } from "../../ComparisonSheet/Utils";
import { emptyCreateModal } from "../../ScheduleStep/constants";

export const loadSheetDetails = (state, action, initialState) => {
  if (requestIsBusy(action) || !requestResponse(action)) {
    return {
      ...state,
      sheetDetailsLoading: requestIsBusy(action),
      loadFailed: !requestIsSuccess(action) && !requestIsBusy(action),
    };
  }

  const response = requestResponse(action);

  const data = setDefaults(
    { ...state.sheetData, ...response },
    response.defaults,
  );

  const calcTableTypesArr = Array.isArray(data.calcTableTypes)
    ? data.calcTableTypes
    : [];
  const hasStandard = data.includeStandardCalcTable;
  const hasSoftwareAssignment = data.includeSoftwareCalcTable;

  const funderTotalCosts = hasStandard
    ? calculateTotalCosts(data.capital.funder)
    : 0;
  const dealerTotalCosts = hasStandard
    ? calculateTotalCosts(data.capital.dealer)
    : 0;
  const cofundedTotalCosts = hasStandard
    ? calculateTotalCosts(data.capital.cofunded)
    : 0;

  const funderTotalInvestment = hasStandard
    ? calculateTotalInvestment(data.capital.funder)
    : 0;
  const dealerTotalInvestment = hasStandard
    ? calculateTotalInvestment(data.capital.dealer)
    : 0;
  const cofundedTotalInvestment = hasStandard
    ? calculateTotalInvestment(data.capital.cofunded)
    : 0;

  const cofundedTotals =
    hasStandard && data.capital.cofundedSelected?.value
      ? calculateTotals(data.capital.cofunded)
      : getWizardSplitTotals();
  const dealerTotals =
    hasStandard && data.capital.dealerFunderSelected?.value
      ? calculateTotals(data.capital.dealer)
      : getWizardSplitTotals();
  const funderTotals =
    hasStandard && data.capital.funderSelected?.value
      ? calculateTotals(data.capital.funder)
      : getWizardSplitTotals();

  const cofundedSoftwareTotals =
    hasSoftwareAssignment && data.capital.cofundedSoftwareSelected?.value
      ? calculateTotals(data.capital.cofundedSoftware)
      : getWizardSplitTotals();
  const dealerSoftwareTotals =
    hasSoftwareAssignment && data.capital.dealerFunderSoftwareSelected?.value
      ? calculateTotals(data.capital.dealerSoftware)
      : getWizardSplitTotals();
  const funderSoftwareTotals =
    hasSoftwareAssignment && data.capital.funderSoftwareSelected?.value
      ? calculateTotals(data.capital.funderSoftware)
      : getWizardSplitTotals();

  updateServicesArrays(data.services);
  const sectionsCompleted = checkSectionsCompleted(data, false);

  const servicesTotal = calculateServicesTotals(
    concatenateServiceArrays(data.services),
    action.customData.serviceData,
    parseFloat(data.capital.totalDevicesOnSchedule.value),
  );

  if (hasStandard) {
    updateSettlementAndCoTerminusArrays(data.capital.funder);
    updateSettlementAndCoTerminusArrays(data.capital.cofunded);
    updateSettlementAndCoTerminusArrays(data.capital.dealer);
  }
  if (data.capital.funderSoftware)
    updateSettlementAndCoTerminusArrays(data.capital.funderSoftware);
  if (data.capital.cofundedSoftware)
    updateSettlementAndCoTerminusArrays(data.capital.cofundedSoftware);
  if (data.capital.dealerSoftware)
    updateSettlementAndCoTerminusArrays(data.capital.dealerSoftware);

  const funderSoftwareTotalCosts = hasSoftwareAssignment
    ? calculateTotalCosts(data.capital.funderSoftware)
    : 0;
  const dealerSoftwareTotalCosts = hasSoftwareAssignment
    ? calculateTotalCosts(data.capital.dealerSoftware)
    : 0;
  const cofundedSoftwareTotalCosts = hasSoftwareAssignment
    ? calculateTotalCosts(data.capital.cofundedSoftware)
    : 0;

  const funderSoftwareTotalInvestment = hasSoftwareAssignment
    ? calculateTotalInvestment(data.capital.funderSoftware)
    : 0;
  const dealerSoftwareTotalInvestment = hasSoftwareAssignment
    ? calculateTotalInvestment(data.capital.dealerSoftware)
    : 0;
  const cofundedSoftwareTotalInvestment = hasSoftwareAssignment
    ? calculateTotalInvestment(data.capital.cofundedSoftware)
    : 0;

  updateLargeFormatScheduleItems(data.largeFormatGroups);

  let mergedCapital = mergeData(state.sheetData.capital, data.capital);
  let mergedRates = mergeData(state.sheetData.rates, data.rates);
  let mergedIpsRates = mergeData(state.sheetData.ipsRates, data.ipsRates);

  if (!hasSoftwareAssignment) {
    ["funderSoftware", "cofundedSoftware", "dealerSoftware"].forEach(
      (prop) => delete mergedCapital[prop],
    );
    ["funderFmvSoftware", "cofundedFmvSoftware", "dealerFmvSoftware"].forEach(
      (prop) => delete mergedRates[prop],
    );
    [
      "funderOverridesSoftware",
      "coFundedOverridesSoftware",
      "dealerFundedOverridesSoftware",
    ].forEach((prop) => delete mergedIpsRates[prop]);
  }
  if (!hasStandard) {
    ["funder", "cofunded", "dealer"].forEach(
      (prop) => delete mergedCapital[prop],
    );
    ["funderFmv", "cofundedFmv", "dealerFmv"].forEach(
      (prop) => delete mergedRates[prop],
    );
    ["funderOverrides", "coFundedOverrides", "dealerFundedOverrides"].forEach(
      (prop) => delete mergedIpsRates[prop],
    );
  }

  const { userType, userArea } = action.customData;

  const newState = produce(state, (draftState) => {
    const d = draftState;

    d.sheetDetailsLoading = false;
    d.ratesCompleted = sectionsCompleted.rates;
    d.capitalCompleted = sectionsCompleted.capital;
    d.servicesCompleted = sectionsCompleted.services;
    d.scheduleCompleted = sectionsCompleted.schedule;
    d.scheduleStabilityCompleted = sectionsCompleted.scheduleStability;
    d.paperCompleted = sectionsCompleted.paper;
    d.paperHasContent = sectionsCompleted.paperHasContent;
    d.largeFormatCompleted = sectionsCompleted.largeFormat;
    d.sheetData = {
      ...data,
      clientName: data.clientName,
      crmCompanyId: data.crmCompanyId,
      impersonatedUser: { ...state.sheetData.impersonatedUser },
      isDirty: false,
      ipsRates: mergedIpsRates,
      rates: mergedRates,
      capital: {
        ...mergedCapital,
        ...(hasStandard && {
          funder: {
            ...mergedCapital.funder,
            settlements: data.capital.funder.settlements,
            settlementsTotal: sumValues(data.capital.funder.settlements),
            adminSettlements: data.capital.funder.adminSettlements,
            adminSettlementsTotal: sumValues(
              data.capital.funder.adminSettlements,
            ),
            ipSettlements: data.capital.funder.ipSettlements,
            ipSettlementsTotal: sumValues(data.capital.funder.ipSettlements),
            coTerminusRentals: data.capital.funder.coTerminusRentals,
            ipCoTerminusRentals: data.capital.funder.ipCoTerminusRentals,
          },
          dealer: {
            ...mergedCapital.dealer,
            settlements: data.capital.dealer.settlements,
            settlementsTotal: sumValues(data.capital.dealer.settlements),
            adminSettlements: data.capital.dealer.adminSettlements,
            adminSettlementsTotal: sumValues(
              data.capital.dealer.adminSettlements,
            ),
            ipSettlements: data.capital.dealer.ipSettlements,
            ipSettlementsTotal: sumValues(data.capital.dealer.ipSettlements),
            coTerminusRentals: data.capital.dealer.coTerminusRentals,
            ipCoTerminusRentals: data.capital.dealer.ipCoTerminusRentals,
          },
          cofunded: {
            ...mergedCapital.cofunded,
            settlements: data.capital.cofunded.settlements,
            settlementsTotal: sumValues(data.capital.cofunded.adminSettlements),
            adminSettlements: data.capital.cofunded.adminSettlements,
            adminSettlementsTotal: sumValues(
              data.capital.cofunded.adminSettlements,
            ),
            ipSettlements: data.capital.cofunded.ipSettlements,
            ipSettlementsTotal: sumValues(data.capital.cofunded.ipSettlements),
            coTerminusRentals: data.capital.cofunded.coTerminusRentals,
            ipCoTerminusRentals: data.capital.cofunded.ipCoTerminusRentals,
          },
        }),
        cofundedTotal: cofundedTotals.getTotal(userType, userArea),
        funderTotal: funderTotals.getTotal(userType, userArea),
        dealerTotal: dealerTotals.getTotal(userType, userArea),
        funderTotalCosts: funderTotalCosts,
        dealerTotalCosts: dealerTotalCosts,
        cofundedTotalCosts: cofundedTotalCosts,
        funderTotalInvestment,
        dealerTotalInvestment,
        cofundedTotalInvestment,
        funderSoftwareTotalCosts,
        dealerSoftwareTotalCosts,
        cofundedSoftwareTotalCosts,
        funderSoftwareTotalInvestment,
        dealerSoftwareTotalInvestment,
        cofundedSoftwareTotalInvestment,
        ...(hasSoftwareAssignment && {
          funderSoftware: {
            ...mergedCapital.funderSoftware,
            settlements: data.capital.funderSoftware.settlements,
            settlementsTotal: sumValues(
              data.capital.funderSoftware.settlements,
            ),
            adminSettlements: data.capital.funderSoftware.adminSettlements,
            adminSettlementsTotal: sumValues(
              data.capital.funderSoftware.adminSettlements,
            ),
            ipSettlements: data.capital.funderSoftware.ipSettlements,
            ipSettlementsTotal: sumValues(
              data.capital.funderSoftware.ipSettlements,
            ),
            coTerminusRentals: data.capital.funderSoftware.coTerminusRentals,
            ipCoTerminusRentals:
              data.capital.funderSoftware.ipCoTerminusRentals,
          },
          dealerSoftware: {
            ...mergedCapital.dealerSoftware,
            settlements: data.capital.dealerSoftware.settlements,
            settlementsTotal: sumValues(
              data.capital.dealerSoftware.settlements,
            ),
            adminSettlements: data.capital.dealerSoftware.adminSettlements,
            adminSettlementsTotal: sumValues(
              data.capital.dealerSoftware.adminSettlements,
            ),
            ipSettlements: data.capital.dealerSoftware.ipSettlements,
            ipSettlementsTotal: sumValues(
              data.capital.dealerSoftware.ipSettlements,
            ),
            coTerminusRentals: data.capital.dealerSoftware.coTerminusRentals,
            ipCoTerminusRentals:
              data.capital.dealerSoftware.ipCoTerminusRentals,
          },
          cofundedSoftware: {
            ...mergedCapital.cofundedSoftware,
            settlements: data.capital.cofundedSoftware.settlements,
            settlementsTotal: sumValues(
              data.capital.cofundedSoftware.settlements,
            ),
            adminSettlements: data.capital.cofundedSoftware.adminSettlements,
            adminSettlementsTotal: sumValues(
              data.capital.cofundedSoftware.adminSettlements,
            ),
            ipSettlements: data.capital.cofundedSoftware.ipSettlements,
            ipSettlementsTotal: sumValues(
              data.capital.cofundedSoftware.ipSettlements,
            ),
            coTerminusRentals: data.capital.cofundedSoftware.coTerminusRentals,
            ipCoTerminusRentals:
              data.capital.cofundedSoftware.ipCoTerminusRentals,
          },
        }),
      },
      services: {
        servicesDisableMinVolsLogic: data.services.servicesDisableMinVolsLogic,
        services: data.services.services,
        adminServices: data.services.adminServices,
        ipServices: data.services.ipServices,
        mandatoryServices: data.services.mandatoryServices,
        servicesTotal: servicesTotal.getTotal(userType, userArea),
      },
      paperInclusive: {
        ...state.sheetData.paperInclusive,
        ...data.paperInclusive,
        isPaperInclusive: {
          ...data.paperInclusive.isPaperInclusive,
          value: !!data.paperInclusive.isPaperInclusive.value,
        },
        isCostEffectiveA3: {
          ...data.paperInclusive.isCostEffectiveA3,
          value: stringifyNullableBool(
            data.paperInclusive.isCostEffectiveA3.value,
          ),
        },
      },
      largeFormatCreateModal: emptyCreateModal,
      comparisonData:
        calculateComparisonSheetData(data, null) || initialState.comparisonData,
    };
    d.comparisonCopies = {
      paperInclusive: data.paperInclusive,
    };

    d.scheduleContent =
      d.sheetData.systemScheduleItems.length +
      getLFPItems(d.sheetData.largeFormatGroups).length;
    const systemScheduleItemsCount = d.sheetData.systemScheduleItems.filter(
      (x) => x.addedAsUtility,
    ).length;

    const largeFormatItems = getLFPItems(d.sheetData.largeFormatGroups);
    d.largeFormatGroupsItems = largeFormatItems;
    const lfpItemsCount = largeFormatItems.length;

    draftState.utilityMeters = systemScheduleItemsCount + lfpItemsCount;
    (d.servicesContent = servicesTotal),
      (d.capitalContent = getWizardSplitTotals(
        dealerTotals.salesperson +
          funderTotals.salesperson +
          cofundedTotals.salesperson +
          dealerSoftwareTotals.salesperson +
          funderSoftwareTotals.salesperson +
          cofundedSoftwareTotals.salesperson,
        dealerTotals.dealerAdmin +
          funderTotals.dealerAdmin +
          cofundedTotals.dealerAdmin +
          dealerSoftwareTotals.dealerAdmin +
          funderSoftwareTotals.dealerAdmin +
          cofundedSoftwareTotals.dealerAdmin,
        dealerTotals.ipsAdmin +
          funderTotals.ipsAdmin +
          cofundedTotals.ipsAdmin +
          dealerSoftwareTotals.ipsAdmin +
          funderSoftwareTotals.ipsAdmin +
          cofundedSoftwareTotals.ipsAdmin,
      ));
  });
  return newState;
};
